// @ts-ignore
import { Box, Grid, Link, Typography } from '@mui/material';
import { useHttp } from 'hooks/use-fetch';
import HealthWaypointChartsSection from 'pages/Dashboard/HealthWaypointsChartsSection';
import WaypointTrackingSection from 'pages/Dashboard/WaypointTrackingSection';
import AppleHealthActivitySummaryChart, {
  DataKey,
} from 'pages/Dashboard/components/AppleHealthActivitySummaryChart';
import AppleHealthChartsWrapper from 'pages/Dashboard/components/AppleHealthChartsWrapper';
import LoggedMoods from 'pages/Dashboard/components/LoggedMoods';
import MoodCardV1 from 'pages/Dashboard/components/MoodCard';
import MoodCardV2 from 'pages/Dashboard/components/MoodCardV2';
import { PatientCtx } from 'pages/Dashboard/constants';
import { fetchPatientWaypointList } from 'pages/Dashboard/services/waypoints.services';
import {
  MoodTrackingAverageResponse,
  MoodTrackingListResponse,
  Mood, MoodV2 } from 'pages/Dashboard/types/moods.types';
import {
  WaypointTrackingAPIResponse,
  WaypointType,
} from 'pages/Dashboard/types/waypoints.types';
import {
  TimeFrameOptions,
  getChartDateRanges,
  getFilteredWaypointsData,
  waypointBarSizeMap } from 'pages/Dashboard/utils/trackingUtils';
import * as React from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import theme from 'theme';
import useMostLoggedMetrics from 'pages/Dashboard/useMostLoggedMetrics';
import useStroopData from 'pages/Dashboard/useStroopData';
import useSomaticScoreData from 'pages/Dashboard/useSomaticScoreData';
import { wayPointsUrls } from 'utils/apiUrls';

type Waypoint = {
  id: string;
  title?: string;
  subTitle?: string;
};

const appleHealthWaypointOrder = [
  'sleepDerived',
  'sleepAppleHealth',
  'activityDerived',
  'activityAppleHealth',
  'stepCountDerived',
  'daylightExposure',
];

const stroopInfoText = 'The Stroop test is a neuropsychological test that measures how well a person can focus on one thing while ignoring others.';
const somaticScoreText = 'The Somatic score reflects how well the patient is doing with things like sleep, daylight exposure, and exercise — areas they can control to improve their overall mental well-being. The score is calculated using Apple Health data.';
interface IPatientTrackingProps {
  timeFrame: string;
  onChangeTimeFrame: (v: string) => void;
  currentMoodData: MoodTrackingAverageResponse;
  previousMoodData: MoodTrackingAverageResponse;
  currentWaypointData: WaypointTrackingAPIResponse;
  previousWaypointData: WaypointTrackingAPIResponse;
  moodChartHappiness: MoodTrackingListResponse;
  moodChartAnxiousness: MoodTrackingListResponse;
  moodChartMotivation: MoodTrackingListResponse;
  isMoodLoading: boolean;
}

export default function PatientTracking({
  timeFrame: selectedTimeFrame,
  onChangeTimeFrame,
  currentMoodData,
  previousMoodData,
  currentWaypointData,
  previousWaypointData,
  moodChartHappiness,
  moodChartAnxiousness,
  moodChartMotivation,
  isMoodLoading,
}: IPatientTrackingProps) {
  const patient = React.useContext(PatientCtx);
  const { http, providerId, isNewVersionEnabled } = useHttp();
  const { id: patientId } = useParams();
  const [moodId, setMoodId] = React.useState('');
  const [dataMap, setDataMap] = React.useState<Record<string, boolean>>({});
  const timeFrame = selectedTimeFrame;

  // Use the custom hook for most logged metrics
  const {
    mostLoggedMetrics,
    hasMostLoggedMetrics,
    moodOptions,
  } = useMostLoggedMetrics({
    providerId,
    patientId,
    selectedTimeFrame,
    isNewVersionEnabled,
    moodId,
    currentMoodData,
    previousMoodData,
  });

  // Use the custom hook for Stroop data
  const {
    stroopTimeSeriesData,
    stroopTimeSeriesAvgData,
    isStroopTimeSeriesDataLoading,
    hasStroop6MData,
  } = useStroopData({
    providerId,
    patientId,
    timeFrame,
  });

  // Use the custom hook for Somatic Score data
  const {
    somaticScoreTimeSeriesData,
    somaticScoreTimeSeriesAvgData,
    isSomaticScoresDataLoading,
  } = useSomaticScoreData({
    providerId,
    patientId,
    timeFrame,
    isNewVersionEnabled,

  });

  const fetchPatientWaypointsResult = useQuery(
    [`${wayPointsUrls.listPatientWaypoints.queryUrl}`, patientId],
    {
      queryFn: fetchPatientWaypointList(http.get),
      enabled: !!providerId && !!patientId,
    },
  );

  const appleHealthWaypointMap = React.useMemo(() => ({} as Record<string, string>), []);
  const appleHealthWaypoints = React.useMemo(
    () => fetchPatientWaypointsResult?.data?.waypoints?.reduce((acc, w, i) => {
      if (w?.waypoint?.type === WaypointType.WAYPOINT_TYPE_DERIVED
         || w?.waypoint?.type === WaypointType.WAYPOINT_TYPE_APPLE_HEALTH) {
        appleHealthWaypointMap[w?.waypoint?.name] = w?.waypoint?.id;
        acc.push({
          id: w?.waypoint?.id,
          title: w?.waypoint?.displayName,
          subTitle: w?.waypoint?.description,
        });
      }

      return acc;
    }, [] as Waypoint[]) || [],
    [appleHealthWaypointMap, fetchPatientWaypointsResult?.data?.waypoints],
  );

  const sortedAppleHealthWaypoints = React.useMemo(
    () => appleHealthWaypoints
      ?.sort((a, b) => appleHealthWaypointOrder
        .indexOf(a.id)
        - appleHealthWaypointOrder.indexOf(b.id)),
    [appleHealthWaypoints],
  );

  React.useEffect(() => {
    if (hasStroop6MData) {
      setDataMap((prev) => ({ ...prev, stroop: true }));
    }
  }, [hasStroop6MData]);

  React.useEffect(() => {
    if (hasMostLoggedMetrics) {
      setDataMap((prev) => ({ ...prev, mostLoggedMetrics: true }));
    }
  }, [hasMostLoggedMetrics]);

  const getChartData = React.useCallback((mood: Mood | MoodV2) => {
    if (mood === Mood.MOOD_HAPPINESS || mood === MoodV2.MOOD_PLEASANTNESS) {
      return moodChartHappiness;
    }
    else if (mood === Mood.MOOD_MOTIVATION || mood === MoodV2.MOOD_ENERGY) {
      return moodChartMotivation;
    }
    return moodChartAnxiousness;
  }, [moodChartHappiness, moodChartMotivation, moodChartAnxiousness]);

  const groupedAllWaypoints = getFilteredWaypointsData(
    currentWaypointData.waypoints,
    previousWaypointData.waypoints,
  );

  const yAxisProps = getChartDateRanges(timeFrame as TimeFrameOptions) as {
    startTime: number;
    endTime: number;
    ticks: number[];
    isInDays: boolean;
  };

  const MoodCard = isNewVersionEnabled ? MoodCardV2 : MoodCardV1;

  const gridSize = React.useMemo(() => {
    if (isNewVersionEnabled) { return (hasMostLoggedMetrics || dataMap.mostLoggedMetrics) ? 4 : 6; }
    return 4;
  }, [dataMap.mostLoggedMetrics, hasMostLoggedMetrics, isNewVersionEnabled]);

  const onSelectMood = (selectedMoodId: string) => {
    setMoodId(selectedMoodId);
  };
  return (
    <Box>
      <>
        {' '}
        <Typography variant='h5' mb={2} fontWeight={400} color={theme.custom.colors.lightTextPrimary}>
          Mood
        </Typography>
        <Grid container spacing={2} mb={3}>
          {moodOptions.map((option) => (
            <MoodCard
              timeFrame={selectedTimeFrame}
              cardData={option}
              chartData={getChartData(option.mood)?.moods || []}
              noData={getChartData(option.mood)?.moods?.length === 0}
              isMoodLoading={isMoodLoading}
              gridSize={gridSize}
            />
          ))}
          {hasMostLoggedMetrics && isNewVersionEnabled && (
          <LoggedMoods
            timeFrame={timeFrame}
            data={mostLoggedMetrics}
            noData={!hasMostLoggedMetrics}
            onChangeTimeFrame={onChangeTimeFrame}
            onSelectMood={onSelectMood}
          />
          )}
        </Grid>
      </>
      <>
        {' '}
        <Typography variant='h5' mb={2} fontWeight={400} color={theme.custom.colors.lightTextPrimary} marginTop={5}>
          Activities
        </Typography>
        {fetchPatientWaypointsResult?.isLoading ? null : (
          <HealthWaypointChartsSection
            waypointsList={sortedAppleHealthWaypoints}
            appleHealthWaypointMap={appleHealthWaypointMap}
            patientId={patientId ?? ''}
            timeFrame={timeFrame as TimeFrameOptions}
            providerId={providerId}
            onChangeTimeFrame={onChangeTimeFrame}
          />
        ) }
        <Grid container spacing={2} justifyContent='center' mb={3}>

          {hasStroop6MData && (
          <AppleHealthChartsWrapper
            timeFrame={timeFrame}
            title='Cognitive performance'
            subTitle='(Stroop test)'
            headerIcon='grain'
            isAppleHealthWaypoint={false}
            gridSize={6}
            showTimeFrame={false}
            isLoading={isStroopTimeSeriesDataLoading}
            noData={stroopTimeSeriesData.length === 0}
            infoIcon
            infoIconText={(
              <Box>
                <Typography mb={2} fontSize={18} fontWeight='normal'>
                  {stroopInfoText}
                </Typography>
                <Link
                  underline='hover'
                  target='_blank'
                  href='https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5388755'
                  fontSize={18}
                  color={theme.custom.colors.primaryMain}
                  fontWeight='normal'
                >
                  Learn more
                </Link>
              </Box>
              )}
            renderChart={(
              <AppleHealthActivitySummaryChart
                chartData={stroopTimeSeriesData}
                dataKey={DataKey.Score}
                units=''
                yAxisProps={yAxisProps}
                yAxisDefinition='Score'
                averageValue={stroopTimeSeriesAvgData}
                showAverages={false}
                barSize={waypointBarSizeMap[`${timeFrame as TimeFrameOptions}-${6}`]}
                sourceOptions={[]}
                sourceId=''
              />
              )}
          />
          )}
          {isNewVersionEnabled && somaticScoreTimeSeriesData.length > 0 && (
          <AppleHealthChartsWrapper
            timeFrame={timeFrame}
            title='Somatic score'
            subTitle='(Healthy habits indicator)'
            headerIcon='graphic_eq'
            isAppleHealthWaypoint={false}
            gridSize={6}
            showTimeFrame={false}
            isLoading={isSomaticScoresDataLoading}
            noData={somaticScoreTimeSeriesData.length === 0}
            infoIcon
            infoIconText={(
              <Box>
                <Typography mb={2} fontSize={18} fontWeight='normal'>
                  {somaticScoreText}
                </Typography>
              </Box>
              )}
            renderChart={(
              <AppleHealthActivitySummaryChart
                chartData={somaticScoreTimeSeriesData}
                dataKey={DataKey.SomaticScore}
                units=''
                yAxisProps={yAxisProps}
                yAxisDefinition='Score'
                averageValue={somaticScoreTimeSeriesAvgData}
                showAverages={false}
                barSize={waypointBarSizeMap[`${timeFrame as TimeFrameOptions}-${6}`]}
                barColor={theme.custom.colors.lightTertiaryMain}
                sourceOptions={[]}
                sourceId=''
              />
              )}
          />
          )}
        </Grid>
      </>
      <WaypointTrackingSection
        patient={patient}
        timeFrame={timeFrame}
        currentWaypoints={currentWaypointData?.waypoints}
        previousWaypoints={previousWaypointData?.waypoints}
        allWaypoints={groupedAllWaypoints}
      />
    </Box>
  );
}
